import * as React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './components/Home';
import Projects from './components/Projects';
import { Curriculum } from './components/Curriculum';
import Contact from './components/Contact';
import Sidebar from './components/Sidebar';
import './global.css';

// Code-split CaseStudy page so it only loads when navigating to /project/:id
const CaseStudy = React.lazy(() => import('./components/CaseStudy'));

/**
 * ScrollToHashManager
 * Handles smooth scrolling to anchor hashes (e.g. #projects)
 * when navigating from other routes or on initial load.
 */
const ScrollToHashManager: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location]);

  return null;
};

/**
 * Main App Component
 * 
 * Root component managing global language state and defining application routes.
 */
function App() {
  // State to manage current language, persisting to localStorage with browser fallback
  const [lang, setLang] = React.useState<'en' | 'it'>(() => {
    try {
      const saved = localStorage.getItem('preferred_lang');
      if (saved === 'en' || saved === 'it') return saved;
      return navigator.language?.toLowerCase().startsWith('it') ? 'it' : 'en';
    } catch {
      return 'en';
    }
  });

  // Keep localStorage and document.documentElement.lang in sync for a11y & SEO
  React.useEffect(() => {
    try {
      localStorage.setItem('preferred_lang', lang);
    } catch {}
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <div className="app-container">
      <ScrollToHashManager />
      {/* Sidebar Navigation - passed lang and setLang to control language toggle */}
      <Sidebar lang={lang} setLang={setLang} />

      {/* Main Content Area */}
      <main className="main-content">
        <React.Suspense
          fallback={
            <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-secondary)' }}>
              Loading...
            </div>
          }
        >
          <Routes>
            {/* Main Portfolio Scroll View */}
            <Route
              path="/"
              element={
                <>
                  <Home lang={lang} />
                  <Projects lang={lang} />
                  <Curriculum lang={lang} />
                  <Contact lang={lang} />
                </>
              }
            />

            {/* Dedicated Individual Case Study Page */}
            <Route path="/project/:id" element={<CaseStudy lang={lang} />} />

            {/* Catch-all route redirecting back to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </React.Suspense>
      </main>
    </div>
  );
}

export default App;
